import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';
import axios from 'axios';
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { 
  Calendar, Clock, MapPin, Plus, X, Users, ExternalLink, 
  Share2, MessageCircle, Facebook, Twitter, ChevronRight,
  Building, FileText, Link as LinkIcon, Trash2, Phone, Check, UserCheck,
  Pencil, Send, UserPlus, History, CalendarCheck
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Componente Mappa per selezionare/visualizzare posizione
const LocationPicker = ({ value, onChange, readOnly = false }) => {
  const defaultCenter = value?.lat ? value : { lat: 45.4384, lng: 10.9916 }; // Verona default

  const handleMapClick = useCallback((e) => {
    if (readOnly) return;
    const lat = e.detail.latLng.lat;
    const lng = e.detail.latLng.lng;
    onChange({ lat, lng });
  }, [onChange, readOnly]);

  return (
    <div className="w-full h-48 rounded-xl overflow-hidden border border-slate-600">
      <APIProvider apiKey={MAPS_API_KEY}>
        <Map
          defaultCenter={defaultCenter}
          defaultZoom={13}
          mapId="location-picker-map"
          onClick={handleMapClick}
          gestureHandling="greedy"
          disableDefaultUI={true}
        >
          {value?.lat && (
            <AdvancedMarker position={value}>
              <div className="w-8 h-8 bg-cyan-500 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
                <MapPin className="w-4 h-4 text-white" />
              </div>
            </AdvancedMarker>
          )}
        </Map>
      </APIProvider>
    </div>
  );
};

// Card Evento
const EventCard = ({ event, onViewDetails, onShare, onDelete, onEdit, onInvite, currentUserId }) => {
  const eventDate = new Date(event.event_date + 'T' + event.event_time);
  const isUpcoming = eventDate > new Date();
  const isOwner = event.user_id === currentUserId;

  return (
    <div className="bg-slate-900/60 backdrop-blur-xl border border-cyan-500/30 rounded-2xl p-5 hover:border-cyan-500/50 transition-all">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            {isUpcoming ? (
              <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-[10px] font-bold rounded-full uppercase">
                In Programma
              </span>
            ) : (
              <span className="px-2 py-0.5 bg-slate-500/20 text-slate-400 text-[10px] font-bold rounded-full uppercase">
                Passato
              </span>
            )}
            {event.participants_count > 0 && (
              <span className="px-2 py-0.5 bg-violet-500/20 text-violet-400 text-[10px] font-bold rounded-full">
                {event.participants_count} partecipanti
              </span>
            )}
          </div>
          <h3 className="text-lg font-bold text-white">{event.title}</h3>
        </div>
        {isOwner && isUpcoming && (
          <div className="flex gap-1">
            <button
              onClick={() => onInvite(event)}
              className="p-2 text-cyan-400 hover:bg-cyan-500/20 rounded-lg transition-all"
              title="Invita partecipanti"
            >
              <UserPlus className="w-4 h-4" />
            </button>
            <button
              onClick={() => onEdit(event)}
              className="p-2 text-amber-400 hover:bg-amber-500/20 rounded-lg transition-all"
              title="Modifica"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(event.id)}
              className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-all"
              title="Elimina"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
        {isOwner && !isUpcoming && (
          <button
            onClick={() => onDelete(event.id)}
            className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-all"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 text-slate-300 text-sm">
          <Building className="w-4 h-4 text-cyan-400" />
          <span>{event.organizer}</span>
        </div>
        <div className="flex items-center gap-2 text-slate-300 text-sm">
          <Calendar className="w-4 h-4 text-cyan-400" />
          <span>{new Date(event.event_date).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
        </div>
        <div className="flex items-center gap-2 text-slate-300 text-sm">
          <Clock className="w-4 h-4 text-cyan-400" />
          <span>{event.event_time}</span>
        </div>
        {event.location_name && (
          <div className="flex items-center gap-2 text-slate-300 text-sm">
            <MapPin className="w-4 h-4 text-cyan-400" />
            <span>{event.location_name}</span>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => onViewDetails(event)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-xl transition-all"
        >
          <span>Vedi Dettagli</span>
          <ChevronRight className="w-4 h-4" />
        </button>
        <button
          onClick={() => onShare(event)}
          className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-all"
        >
          <Share2 className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// Modal Dettagli Evento
const EventDetailsModal = ({ event, onClose, onParticipationChange }) => {
  const { token } = useAuth();
  const [participating, setParticipating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [participantsCount, setParticipantsCount] = useState(event?.participants_count || 0);

  useEffect(() => {
    if (!event) return;
    
    const checkParticipation = async () => {
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/events/${event.id}/my-participation`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setParticipating(response.data.participating);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    
    checkParticipation();
  }, [event, token]);

  const handleParticipate = async (willParticipate) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/events/${event.id}/participate`,
        { participating: willParticipate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setParticipating(willParticipate);
      setParticipantsCount(prev => willParticipate ? prev + 1 : Math.max(0, prev - 1));
      
      if (willParticipate) {
        toast.success('Partecipazione confermata!');
        
        // Se c'è un numero di telefono, apri SMS
        if (event.contact_phone) {
          const smsBody = encodeURIComponent(
            `Ciao! Confermo la mia partecipazione all'evento "${event.title}" del ${new Date(event.event_date).toLocaleDateString('it-IT')}. - Respiro Blu`
          );
          window.open(`sms:${event.contact_phone}?body=${smsBody}`, '_blank');
        }
        
        if (onParticipationChange) onParticipationChange();
      } else {
        toast.info('Partecipazione annullata');
        if (onParticipationChange) onParticipationChange();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore nella registrazione');
    }
  };

  if (!event) return null;

  const openInMaps = () => {
    if (event.location?.lat) {
      window.open(`https://www.google.com/maps?q=${event.location.lat},${event.location.lng}`, '_blank');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-cyan-500/50 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">{event.title}</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-xl">
            <Building className="w-5 h-5 text-cyan-400" />
            <div>
              <p className="text-xs text-slate-500 uppercase">Organizzatore</p>
              <p className="text-white font-medium">{event.organizer}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-xl">
              <Calendar className="w-5 h-5 text-cyan-400" />
              <div>
                <p className="text-xs text-slate-500 uppercase">Data</p>
                <p className="text-white font-medium">{new Date(event.event_date).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-xl">
              <Clock className="w-5 h-5 text-cyan-400" />
              <div>
                <p className="text-xs text-slate-500 uppercase">Orario</p>
                <p className="text-white font-medium">{event.event_time}</p>
              </div>
            </div>
          </div>

          {/* Contatore Partecipanti */}
          <div className="flex items-center gap-3 p-3 bg-violet-500/10 border border-violet-500/30 rounded-xl">
            <UserCheck className="w-5 h-5 text-violet-400" />
            <div>
              <p className="text-xs text-slate-500 uppercase">Partecipanti Confermati</p>
              <p className="text-violet-400 font-bold text-lg">{participantsCount}</p>
            </div>
          </div>

          {/* Mappa Punto di Ritrovo */}
          <div>
            <p className="text-sm text-slate-400 mb-2 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-cyan-400" />
              Punto di Ritrovo
              {event.location_name && <span className="text-white">- {event.location_name}</span>}
            </p>
            {event.location?.lat && (
              <>
                <LocationPicker value={event.location} onChange={() => {}} readOnly={true} />
                <button
                  onClick={openInMaps}
                  className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-xl hover:bg-emerald-500/30 transition-all"
                >
                  <MapPin className="w-4 h-4" />
                  Apri in Google Maps
                </button>
              </>
            )}
          </div>

          {/* Descrizione */}
          <div>
            <p className="text-sm text-slate-400 mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4 text-cyan-400" />
              Descrizione e Modalità
            </p>
            <div className="p-4 bg-slate-800/50 rounded-xl">
              <p className="text-slate-300 text-sm whitespace-pre-wrap">{event.description}</p>
            </div>
          </div>

          {/* Telefono Contatto */}
          {event.contact_phone && (
            <a
              href={`tel:${event.contact_phone}`}
              className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-emerald-500/20 text-emerald-400 rounded-xl hover:bg-emerald-500/30 transition-all"
            >
              <Phone className="w-4 h-4" />
              Chiama: {event.contact_phone}
            </a>
          )}

          {/* Link Esterno */}
          {event.external_link && (
            <a
              href={event.external_link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-violet-500/20 text-violet-400 rounded-xl hover:bg-violet-500/30 transition-all"
            >
              <ExternalLink className="w-4 h-4" />
              Iscrizione / Info Esterne
            </a>
          )}

          {/* PULSANTI PARTECIPAZIONE */}
          <div className="pt-4 border-t border-slate-700">
            <p className="text-sm text-slate-400 mb-3 text-center">Partecipi a questo evento?</p>
            
            {loading ? (
              <div className="flex justify-center py-4">
                <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleParticipate(true)}
                  disabled={participating}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold transition-all ${
                    participating 
                      ? 'bg-emerald-500 text-white cursor-default' 
                      : 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500 hover:text-white'
                  }`}
                >
                  <Check className="w-5 h-5" />
                  {participating ? 'Parteciperò!' : 'Sì, Parteciperò'}
                </button>
                <button
                  onClick={() => handleParticipate(false)}
                  disabled={!participating}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold transition-all ${
                    !participating 
                      ? 'bg-slate-700 text-slate-400 cursor-default' 
                      : 'bg-red-500/20 text-red-400 hover:bg-red-500 hover:text-white'
                  }`}
                >
                  <X className="w-5 h-5" />
                  {!participating ? 'Non Partecipo' : 'Annulla'}
                </button>
              </div>
            )}
            
            {participating && event.contact_phone && (
              <p className="text-xs text-emerald-400 text-center mt-3">
                SMS di conferma inviato a {event.contact_phone}
              </p>
            )}
          </div>

          <p className="text-xs text-slate-500 text-center">
            Creato da {event.created_by}
          </p>
        </div>
      </div>
    </div>
  );
};

// Modal Condivisione
const ShareModal = ({ event, onClose }) => {
  if (!event) return null;

  const shareText = `🌊 EVENTO RESPIRO BLU\n\n` +
    `📌 ${event.title}\n` +
    `🏛️ Organizzato da: ${event.organizer}\n` +
    `📅 ${new Date(event.event_date).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}\n` +
    `⏰ Ore ${event.event_time}\n` +
    `📍 ${event.location_name || 'Vedi mappa per il punto di ritrovo'}\n\n` +
    `Partecipa anche tu! 🐢\n` +
    `#RespiroBlu #VolontariatoAmbientale #MarineLitter`;

  const shareUrl = event.external_link || 'https://respiro-dashboard.emergent.host/';

  const shareOn = (platform) => {
    let url = '';
    switch (platform) {
      case 'whatsapp':
        url = `https://wa.me/?text=${encodeURIComponent(shareText + '\n' + shareUrl)}`;
        break;
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(shareText + '\n' + shareUrl);
        toast.success('Testo copiato!');
        onClose();
        return;
      default:
        return;
    }
    window.open(url, '_blank', 'width=600,height=400');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-cyan-500/50 rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">Condividi Evento</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        <div className="p-4 bg-slate-800/50 rounded-xl mb-4">
          <p className="text-white font-bold">{event.title}</p>
          <p className="text-slate-400 text-sm">{event.organizer}</p>
          <p className="text-cyan-400 text-sm">
            {new Date(event.event_date).toLocaleDateString('it-IT')} - {event.event_time}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => shareOn('whatsapp')}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-all"
          >
            <MessageCircle className="w-5 h-5" />
            WhatsApp
          </button>
          <button
            onClick={() => shareOn('facebook')}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all"
          >
            <Facebook className="w-5 h-5" />
            Facebook
          </button>
          <button
            onClick={() => shareOn('twitter')}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-sky-500 hover:bg-sky-600 text-white rounded-xl transition-all"
          >
            <Twitter className="w-5 h-5" />
            Twitter/X
          </button>
          <button
            onClick={() => shareOn('copy')}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-all"
          >
            <LinkIcon className="w-5 h-5" />
            Copia
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal Creazione Evento
const CreateEventModal = ({ onClose, onCreated }) => {
  const { token } = useAuth();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    organizer: '',
    event_date: '',
    event_time: '',
    location: null,
    location_name: '',
    description: '',
    external_link: '',
    contact_phone: ''
  });

  const handleSubmit = async () => {
    if (!form.title || !form.organizer || !form.event_date || !form.event_time || !form.description) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    if (!form.location?.lat) {
      toast.error('Seleziona il punto di ritrovo sulla mappa');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/events`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Evento pubblicato!');
      onCreated();
      onClose();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore nella creazione');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-cyan-500/50 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-cyan-400" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>CREA ATTIVITÀ</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Titolo */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider mb-1 block">
              Titolo Evento *
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="es: Pulizia Spiaggia dei Gulli"
              className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white focus:border-cyan-500 focus:outline-none"
            />
          </div>

          {/* Organizzatore */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider mb-1 block">
              Ente Organizzatore *
            </label>
            <input
              type="text"
              value={form.organizer}
              onChange={(e) => setForm({ ...form, organizer: e.target.value })}
              placeholder="es: Associazione Mare Pulito"
              className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white focus:border-cyan-500 focus:outline-none"
            />
          </div>

          {/* Data e Ora */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider mb-1 block">
                Data *
              </label>
              <input
                type="date"
                value={form.event_date}
                onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white focus:border-cyan-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider mb-1 block">
                Orario *
              </label>
              <input
                type="time"
                value={form.event_time}
                onChange={(e) => setForm({ ...form, event_time: e.target.value })}
                className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white focus:border-cyan-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Nome Luogo */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider mb-1 block">
              Nome del Punto di Ritrovo
            </label>
            <input
              type="text"
              value={form.location_name}
              onChange={(e) => setForm({ ...form, location_name: e.target.value })}
              placeholder="es: Parcheggio Lungomare"
              className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white focus:border-cyan-500 focus:outline-none"
            />
          </div>

          {/* Mappa */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider mb-1 block">
              Punto di Ritrovo (clicca sulla mappa) *
            </label>
            <LocationPicker 
              value={form.location} 
              onChange={(loc) => setForm({ ...form, location: loc })}
            />
            {form.location?.lat && (
              <p className="text-xs text-emerald-400 mt-1">
                ✓ Posizione selezionata: {form.location.lat.toFixed(5)}, {form.location.lng.toFixed(5)}
              </p>
            )}
          </div>

          {/* Descrizione */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider mb-1 block">
              Descrizione e Modalità *
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Cosa portare, contatti, programma della giornata..."
              rows={4}
              className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white focus:border-cyan-500 focus:outline-none resize-none"
            />
          </div>

          {/* Link Esterno */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider mb-1 block">
              Link Esterno (opzionale)
            </label>
            <input
              type="url"
              value={form.external_link}
              onChange={(e) => setForm({ ...form, external_link: e.target.value })}
              placeholder="https://..."
              className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white focus:border-cyan-500 focus:outline-none"
            />
          </div>

          {/* Telefono per Adesioni */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider mb-1 block">
              Telefono per Adesioni (opzionale)
            </label>
            <input
              type="tel"
              value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
              placeholder="+39 333 1234567"
              className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white focus:border-cyan-500 focus:outline-none"
            />
            <p className="text-xs text-slate-500 mt-1">I partecipanti potranno inviare SMS di adesione</p>
          </div>

          {/* Pulsante Pubblica */}
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="w-full py-4 bg-cyan-500 hover:bg-cyan-600 text-white font-bold rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Pubblicazione...
              </>
            ) : (
              <>
                <Users className="w-5 h-5" />
                Pubblica Attività
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal Modifica Evento
const EditEventModal = ({ event, onClose, onUpdated }) => {
  const { token } = useAuth();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    event_date: event.event_date || '',
    event_time: event.event_time || '',
    location: event.location || null,
    location_name: event.location_name || ''
  });

  const handleSubmit = async () => {
    if (!form.event_date || !form.event_time) {
      toast.error('Data e orario sono obbligatori');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/events/${event.id}`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Evento aggiornato!');
      onUpdated();
      onClose();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-amber-500/50 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-amber-400" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>MODIFICA ATTIVITÀ</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-slate-800/50 rounded-xl">
          <p className="text-white font-semibold">{event.title}</p>
          <p className="text-slate-400 text-sm">{event.organizer}</p>
        </div>

        <div className="space-y-4">
          {/* Data */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider mb-1 block">
              Nuova Data
            </label>
            <input
              type="date"
              value={form.event_date}
              onChange={(e) => setForm({ ...form, event_date: e.target.value })}
              className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white focus:border-amber-500 focus:outline-none"
            />
          </div>

          {/* Orario */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider mb-1 block">
              Nuovo Orario
            </label>
            <input
              type="time"
              value={form.event_time}
              onChange={(e) => setForm({ ...form, event_time: e.target.value })}
              className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white focus:border-amber-500 focus:outline-none"
            />
          </div>

          {/* Luogo di Ritrovo */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider mb-1 block">
              Nuovo Luogo di Ritrovo (opzionale)
            </label>
            <input
              type="text"
              value={form.location_name}
              onChange={(e) => setForm({ ...form, location_name: e.target.value })}
              placeholder="es: Bar sulla spiaggia"
              className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white focus:border-amber-500 focus:outline-none mb-2"
            />
            <LocationPicker
              value={form.location}
              onChange={(loc) => setForm({ ...form, location: loc })}
            />
          </div>

          {/* Pulsante Salva */}
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="w-full py-4 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Salvataggio...
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Salva Modifiche
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal Invita Partecipanti
const InviteModal = ({ event, onClose }) => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);

  useEffect(() => {
    const fetchInvitableUsers = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/events/${event.id}/invitable-users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUsers(response.data);
      } catch (error) {
        console.error('Error:', error);
        toast.error('Errore nel caricamento utenti');
      } finally {
        setLoading(false);
      }
    };
    fetchInvitableUsers();
  }, [event.id, token]);

  const handleInviteSelected = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Seleziona almeno un utente');
      return;
    }

    setInviting(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/events/${event.id}/invite`,
        { user_ids: selectedUsers, invite_all: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      onClose();
    } catch (error) {
      toast.error('Errore nell\'invio inviti');
    } finally {
      setInviting(false);
    }
  };

  const handleInviteAll = async () => {
    if (!window.confirm('Vuoi invitare tutti gli utenti della community che accettano inviti?')) return;

    setInviting(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/events/${event.id}/invite`,
        { user_ids: [], invite_all: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      onClose();
    } catch (error) {
      toast.error('Errore nell\'invio inviti');
    } finally {
      setInviting(false);
    }
  };

  const toggleUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const availableUsers = users.filter(u => !u.already_invited);
  const invitedUsers = users.filter(u => u.already_invited);

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-cyan-500/50 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-cyan-400" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>INVITA PARTECIPANTI</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-slate-800/50 rounded-xl">
          <p className="text-white font-semibold">{event.title}</p>
          <p className="text-slate-400 text-sm">{new Date(event.event_date).toLocaleDateString('it-IT')} alle {event.event_time}</p>
        </div>

        {/* Pulsante Invita Tutti */}
        <button
          onClick={handleInviteAll}
          disabled={inviting || availableUsers.length === 0}
          className="w-full py-3 mb-4 bg-violet-500 hover:bg-violet-600 text-white font-semibold rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Send className="w-4 h-4" />
          Invita tutta la community ({availableUsers.length})
        </button>

        <div className="text-center text-slate-500 text-xs mb-4">oppure seleziona singoli utenti</div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : availableUsers.length === 0 ? (
          <div className="text-center py-8">
            <UserPlus className="w-12 h-12 text-slate-600 mx-auto mb-2" />
            <p className="text-slate-400">Tutti gli utenti sono già stati invitati</p>
          </div>
        ) : (
          <>
            <div className="space-y-2 max-h-64 overflow-y-auto mb-4">
              {availableUsers.map(u => (
                <div 
                  key={u.user_id}
                  onClick={() => toggleUser(u.user_id)}
                  className={`p-3 rounded-xl cursor-pointer transition-all flex items-center gap-3 ${
                    selectedUsers.includes(u.user_id) 
                      ? 'bg-cyan-500/20 border border-cyan-500/50' 
                      : 'bg-slate-800/50 border border-slate-700 hover:border-slate-600'
                  }`}
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    selectedUsers.includes(u.user_id) ? 'bg-cyan-500 border-cyan-500' : 'border-slate-500'
                  }`}>
                    {selectedUsers.includes(u.user_id) && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">{u.name || 'Utente'}</p>
                    {u.organization_name && (
                      <p className="text-slate-400 text-xs">{u.organization_name}</p>
                    )}
                  </div>
                  <span className={`px-2 py-0.5 text-[10px] rounded ${
                    u.user_type === 'volontario' ? 'bg-cyan-500/20 text-cyan-400' :
                    u.user_type === 'peschereccio' ? 'bg-orange-500/20 text-orange-400' :
                    'bg-violet-500/20 text-violet-400'
                  }`}>
                    {u.user_type === 'volontario' ? 'Volontario' :
                     u.user_type === 'peschereccio' ? 'Peschereccio' :
                     u.user_type === 'azienda' ? 'Azienda' :
                     u.user_type || 'Utente'}
                  </span>
                </div>
              ))}
            </div>

            {/* Pulsante Invia Inviti Selezionati */}
            {selectedUsers.length > 0 && (
              <button
                onClick={handleInviteSelected}
                disabled={inviting}
                className="w-full py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {inviting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <UserPlus className="w-4 h-4" />
                    Invita {selectedUsers.length} selezionati
                  </>
                )}
              </button>
            )}
          </>
        )}

        {/* Lista già invitati */}
        {invitedUsers.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Già invitati ({invitedUsers.length})</p>
            <div className="flex flex-wrap gap-2">
              {invitedUsers.map(u => (
                <span key={u.user_id} className="px-2 py-1 bg-slate-800 text-slate-400 text-xs rounded-lg">
                  {u.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Principale
const AttivitaSection = () => {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [shareEvent, setShareEvent] = useState(null);
  const [editEvent, setEditEvent] = useState(null);
  const [inviteEvent, setInviteEvent] = useState(null);
  const [activeTab, setActiveTab] = useState('upcoming'); // 'upcoming' o 'past'

  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleDelete = async (eventId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo evento?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Evento eliminato');
      fetchEvents();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  // Filtra eventi per tab
  const now = new Date();
  const upcomingEvents = events.filter(e => new Date(e.event_date + 'T' + e.event_time) >= now);
  const pastEvents = events.filter(e => new Date(e.event_date + 'T' + e.event_time) < now);
  const displayedEvents = activeTab === 'upcoming' ? upcomingEvents : pastEvents;

  return (
    <div className="max-w-4xl mx-auto p-6 pb-24">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="flex justify-center mb-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/likcbe5b_IMG_6596.jpeg" 
            alt="Respiro Blu Logo" 
            className="w-24 h-24 rounded-2xl object-cover shadow-[0_0_30px_rgba(6,182,212,0.3)]"
          />
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
          ATTIVITÀ
        </h2>
        <p className="text-slate-400 mt-2">Eventi e iniziative di volontariato</p>
      </div>

      {/* Tab Switcher */}
      <div className="flex bg-slate-900/60 rounded-xl p-1 mb-6">
        <button
          onClick={() => setActiveTab('upcoming')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-semibold transition-all ${
            activeTab === 'upcoming' 
              ? 'bg-cyan-500 text-white' 
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <CalendarCheck className="w-4 h-4" />
          <span>In Programma</span>
          {upcomingEvents.length > 0 && (
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              activeTab === 'upcoming' ? 'bg-white/20' : 'bg-cyan-500/20 text-cyan-400'
            }`}>
              {upcomingEvents.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('past')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-semibold transition-all ${
            activeTab === 'past' 
              ? 'bg-slate-600 text-white' 
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <History className="w-4 h-4" />
          <span>Passate</span>
          {pastEvents.length > 0 && (
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              activeTab === 'past' ? 'bg-white/20' : 'bg-slate-500/20 text-slate-400'
            }`}>
              {pastEvents.length}
            </span>
          )}
        </button>
      </div>

      {/* Lista Eventi */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : displayedEvents.length === 0 ? (
        <div className="text-center py-16">
          {activeTab === 'upcoming' ? (
            <>
              <Calendar className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">Nessuna attività in programma</p>
              <p className="text-slate-500 text-sm mt-2">Crea la prima attività cliccando sul pulsante +</p>
            </>
          ) : (
            <>
              <History className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">Nessuna attività passata</p>
              <p className="text-slate-500 text-sm mt-2">Le attività concluse appariranno qui</p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {displayedEvents.map(event => (
            <EventCard
              key={event.id}
              event={event}
              currentUserId={user?.uid}
              onViewDetails={setSelectedEvent}
              onShare={setShareEvent}
              onDelete={handleDelete}
              onEdit={setEditEvent}
              onInvite={setInviteEvent}
            />
          ))}
        </div>
      )}

      {/* FAB Crea Attività */}
      <button
        onClick={() => setShowCreateModal(true)}
        className="fixed bottom-24 right-6 w-14 h-14 bg-cyan-500 hover:bg-cyan-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 z-40"
        data-testid="create-event-fab"
      >
        <Plus className="w-7 h-7" />
      </button>

      {/* Modals */}
      {showCreateModal && (
        <CreateEventModal 
          onClose={() => setShowCreateModal(false)} 
          onCreated={fetchEvents}
        />
      )}

      {selectedEvent && (
        <EventDetailsModal 
          event={selectedEvent} 
          onClose={() => setSelectedEvent(null)}
          onParticipationChange={fetchEvents}
        />
      )}

      {shareEvent && (
        <ShareModal 
          event={shareEvent} 
          onClose={() => setShareEvent(null)} 
        />
      )}

      {editEvent && (
        <EditEventModal 
          event={editEvent} 
          onClose={() => setEditEvent(null)}
          onUpdated={fetchEvents}
        />
      )}

      {inviteEvent && (
        <InviteModal 
          event={inviteEvent} 
          onClose={() => setInviteEvent(null)}
        />
      )}
    </div>
  );
};

export default AttivitaSection;
